import React from 'react';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../../i18n/TModules';
import { WizardEditTarget } from '../wizardModel';
import { LinkButton, tint, wz, wzType } from '../wizardStyles';

// 6778:21049 `rtable`: r12 around 260-wide key cells on the field fill; values on the panel.
const Table = styled.dl`
  margin: 0;
  overflow: hidden;
  border: ${wz.line} solid ${wz.border};
  border-radius: 12px;
`;

const Row = styled.div`
  display: flex;

  & + & {
    border-top: ${wz.line} solid ${wz.border};
  }
`;

const Key = styled.dt`
  flex: 0 0 260px;
  padding: 15px 20px;
  border-right: ${wz.line} solid ${wz.border};
  background: ${wz.field};
  color: ${wz.muted};
  ${wzType.input}
`;

const Value = styled.dd`
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  padding: 15px 20px;
  color: ${wz.text};
  font-size: 14px;
  line-height: 23.8px;
  white-space: pre-line;
`;

const EditCell = styled.div`
  flex: none;
  padding: 12px 16px 0 0;
`;

// 6778:21067 `tagf`: the pill after the card's last slot.
export const FinalSlotTag = styled.span`
  display: inline-block;
  margin-left: 10px;
  padding: 3px 9px;
  border-radius: 999px;
  background: ${tint(wz.success, 0.14)};
  color: ${wz.success};
  font-size: 11px;
  font-weight: 800;
  line-height: 12.8px;
  vertical-align: middle;
`;

export interface IReviewRow<E = WizardEditTarget> {
  key: string;
  label: string;
  value: React.ReactNode;
  edit?: E;
}

interface IReviewTableProps<E> {
  rows: IReviewRow<E>[];
  /** Back to the step a row came from, answers kept. */
  onEdit?: (target: E) => void;
}

const ReviewTable = <E,>({ rows, onEdit }: IReviewTableProps<E>) => {
  const { t } = useMultiLanguageTranslation();
  return (
    <Table data-testid="orbit-wizard-review-table">
      {rows.map(row => (
        <Row key={row.key}>
          <Key>{row.label}</Key>
          <Value>{row.value}</Value>
          {onEdit !== undefined && row.edit !== undefined ? (
            <EditCell>
              <LinkButton
                type="button"
                aria-label={t(`${TOrbit}.wizard.editSection`, { section: row.label })}
                onClick={() => {
                  if (row.edit !== undefined) onEdit(row.edit);
                }}
              >
                {t(`${TOrbit}.wizard.edit`)}
              </LinkButton>
            </EditCell>
          ) : null}
        </Row>
      ))}
    </Table>
  );
};

export default ReviewTable;
